import type { GeoJsonFeatureCollection } from '../map/contracts.ts';

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function validatePosition(value: unknown, path: string): void {
  if (
    !Array.isArray(value) ||
    value.length < 2 ||
    !value.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate))
  ) {
    throw new TypeError(`${path} must be a finite GeoJSON position`);
  }
  const longitude: unknown = value[0];
  const latitude: unknown = value[1];
  if (
    typeof longitude !== 'number' ||
    typeof latitude !== 'number' ||
    longitude < -180 ||
    longitude > 180 ||
    latitude < -90 ||
    latitude > 90
  ) {
    throw new RangeError(`${path} must contain WGS84 longitude and latitude`);
  }
}

function validatePositions(value: unknown, depth: number, path: string): void {
  if (depth === 0) {
    validatePosition(value, path);
    return;
  }
  if (!Array.isArray(value) || value.length === 0) {
    throw new TypeError(`${path} must be a non-empty coordinate array`);
  }
  value.forEach((coordinates, index) => {
    validatePositions(coordinates, depth - 1, `${path}[${String(index)}]`);
  });
}

function validateGeometry(geometry: Readonly<Record<string, unknown>>, path: string): void {
  const depthByType = {
    Point: 0,
    MultiPoint: 1,
    LineString: 1,
    MultiLineString: 2,
    Polygon: 2,
    MultiPolygon: 3,
  } as const;
  const type = geometry['type'];
  if (typeof type !== 'string' || !(type in depthByType)) {
    throw new TypeError(`${path}.type is unsupported`);
  }
  validatePositions(
    geometry['coordinates'],
    depthByType[type as keyof typeof depthByType],
    `${path}.coordinates`,
  );
  if (type === 'LineString' && (geometry['coordinates'] as readonly unknown[]).length < 2) {
    throw new TypeError(`${path}.coordinates must contain at least two positions`);
  }
}

export function parseGeoJsonFeatureCollection(
  value: unknown,
  sourceName: string,
): GeoJsonFeatureCollection {
  if (
    !isRecord(value) ||
    value['type'] !== 'FeatureCollection' ||
    !Array.isArray(value['features']) ||
    'crs' in value
  ) {
    throw new TypeError(`${sourceName} must be an RFC 7946 GeoJSON FeatureCollection`);
  }
  for (const [index, feature] of value['features'].entries()) {
    if (
      !isRecord(feature) ||
      feature['type'] !== 'Feature' ||
      !isRecord(feature['geometry']) ||
      !('coordinates' in feature['geometry']) ||
      (feature['properties'] !== null && !isRecord(feature['properties']))
    ) {
      throw new TypeError(`${sourceName}.features[${String(index)}] must be a GeoJSON Feature`);
    }
    validateGeometry(feature['geometry'], `${sourceName}.features[${String(index)}].geometry`);
  }
  return Object.freeze({ type: 'FeatureCollection', features: Object.freeze(value['features']) });
}
